import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { AjaxListingContext, AjaxListingPagination } from '$partials/base/ajax-listing.jsx';
import {
    Footer,
    HeaderLanding,
    LandingHero,
    Meta
} from '$partials/global/index.js';
import { useAjaxListing } from '$hooks/ajax-listing';
import { ArticleStandardListingItem } from '$partials/listings/items';
import { useLocale } from '$contexts/locale.jsx';
import { ModuleLargeStatement, ModuleRelatedCarousel } from '$partials/modules/individual/index.js';
import { GlobalLayoutProvider, GlobalLayoutContext } from '$contexts/global-layout.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

const FilterList = ({
    appliedFilter,
    filterOption,
    index,
    setFilter,
    totalItems
}) => {
    const { translate: t } = useLocale();
    let isCurrent = appliedFilter.title === filterOption.title;
    const isFirst = index === 0;
    const isLast = index === totalItems - 1;

    const positionClasses = isFirst
        ? 'pr-20 left-0'
        : isLast
            ? 'px-20 left-0'
            : 'px-20 left-1/2 -translate-x-1/2';

    return (
        <div className='text-center'>
            <button
                className='type-style-7'
                onClick={() => setFilter(filterOption)}
                type='button'>
                {t(filterOption.title)}
            </button>
            {isCurrent &&
                <div className={`w-full z-50 rounded-full h-5 -bottom-5 absolute mt-5 ${positionClasses}`} >
                    <div className='w-full h-full rounded-full bg-strava-orange' />
                </div>

            }

            {!isCurrent &&
                <div className={`w-full invisible group-hover:visible z-50 rounded-full h-5 -bottom-5 absolute mt-5 ${positionClasses}`}>
                    <div className='w-full h-full rounded-full bg-strava-orange' />
                </div>

            }
        </div>
    );
};

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const ResourcesListingPage = ({ data, domain }) => {
    const [stylingWhite, setStylingWhite] = useState(true);

    const page = data?.page;

    const {
        current: locale,
        translate: t
    } = useLocale();

    const [filter, setFilter] = useState(data.categories?.find(category => category.slug === data?.items?.filters?.category));

    let query = { locale: locale?.code, category: filter?.slug };

    const listing = useAjaxListing({
        items: data?.items?.items,
        page: data?.items?.page || 1,
        path: '/api/v1/listings/resources',
        totalItems: data?.items?.total,
        totalPages: data?.items?.totalPages,
        typename: page.__typename,
        query
    });

    return (
        <>
            <Meta page={page} />

            <main
                className='min-h-screen relative'
                id='main-content'>
                <GlobalLayoutProvider>
                    <GlobalLayoutContext.Consumer>
                        {() => (
                            <>
                                <HeaderLanding
                                    menu={data?.navigation}
                                    setStylingWhite={setStylingWhite}
                                    stylingWhite={stylingWhite} />

                                <LandingHero
                                    ctaLabel={page?.heroCtaLabel}
                                    ctaLink={page?.heroCtaLink}
                                    headerBackgroundImage={page?.heroMedia}
                                    heroImageCropLeft={page?.heroImageCropLeft}
                                    mainText={page?.heroTitle}
                                    tickerText={page?.tickerText} />
                            </>
                        )}
                    </GlobalLayoutContext.Consumer>
                </GlobalLayoutProvider>

                <div className='md:-mt-60'>
                    {/* Related Items */}
                    {page.relatedItems?.items?.length > 0 && (
                        <div className='relative bg-white transform-gpu'>
                            <ModuleRelatedCarousel
                                items={page.relatedItems.items}
                                parent={'Listing'}
                                title={page.relatedTitle} />
                        </div>
                    )}

                    <div className='pt-20 relative bg-white transform-gpu'>
                        <article>

                            <ModuleLargeStatement
                                eyebrowTitle={page?.largeStatementEyebrow}
                                title={page?.largeStatementTitle} />

                            <div className='bg-white relative'>
                                <div className='layout-container'>
                                    <div className='px-10 sm:px-5 md:px-10 pt-60 md:pt-80'>
                                        <h2 className='type-style-3 w-full sm:w-4/6 md:w-10/12'>
                                            {t('All resources')}
                                        </h2>
                                        {data.categories?.length > 1 && page.displayFilters && (
                                            <div className='type-style-7 pt-30 md:pt-50'>
                                                {data.categories.length > 1 && (
                                                    <div className='flex flex-col -mx-20 sm:-mx-40 md:mx-0'>
                                                        <div className=''>
                                                            <div className='
                                                                w-full
                                                                max-w-screen
                                                                md:max-w-[1140px]
                                                                overflow-x-auto
                                                                scrollbar-hide
                                                                pl-20 sm:pl-40 md:pl-0
                                                            '>
                                                                <div className='inline-block w-max overflow-y-hidden'>
                                                                    <ul className='
                                                                        list-none
                                                                        flex
                                                                        pb-20
                                                                        categories-list
                                                                    '>
                                                                        {data.categories.map((item, idx) => (
                                                                            <li
                                                                                className={`group inline-block relative flex-shrink-0 ${!idx ? 'pr-20' : 'px-20'} pb-15`}
                                                                                key={item.slug}>
                                                                                <FilterList
                                                                                    appliedFilter={filter}
                                                                                    filterOption={item}
                                                                                    index={idx}
                                                                                    setFilter={setFilter}
                                                                                    totalItems={data.categories.length} />
                                                                            </li>
                                                                        ))}
                                                                    </ul>

                                                                    <div className='w-full bg-sand -mt-20 h-5 rounded-full' />
                                                                </div>
                                                            </div>

                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className='bg-white relative'>
                                <div className='layout-container'>

                                    {/* Listing */}
                                    <AjaxListingContext.Provider value={listing}>
                                        <section
                                            aria-busy={listing.loading}
                                            aria-label={t('Articles')}
                                            className='flex flex-wrap pt-60 md:pt-80'
                                            role='feed'>
                                            {listing?.items?.length > 0 ? (listing?.items?.map((item, i) => {
                                                if (!item) { return null; }
                                                return (
                                                    <article
                                                        aria-label={item.title}
                                                        aria-posinset={i + 1}
                                                        aria-setsize={listing.totalItems}
                                                        className={'flex flex-col w-full sm:w-6/12 md:w-4/12 pb-50 md:pb-70 px-10 sm:px-5 md:px-10'}
                                                        key={`${item.sys?.id}-${i}`}
                                                        tabIndex={-1}>
                                                        <ArticleStandardListingItem
                                                            {...item}
                                                            tags={item.categories}
                                                            url={`/resources/${item.slug}`} />
                                                    </article>
                                                );
                                            })) : (
                                                <div className='px-10 sm:px-5 md:px-10 mb-60'>No results</div>
                                            )}

                                            {listing.totalPages > 1 && <div className='border-b border-dark-grey mx-10 sm:mx-5 md:mx-10 w-full' />}
                                        </section>
                                        {listing.totalPages > 1 && (
                                            <div className='w-full px-10 sm:px-5 md:px-10 mt-30 mb-20 sm:mb-0 flex align items-start'>
                                                <AjaxListingPagination
                                                    current={listing.page}
                                                    totalPages={listing.totalPages} />
                                            </div>
                                        )}
                                    </AjaxListingContext.Provider>
                                </div>
                            </div>
                        </article>
                        <div className='bg-white relative'>
                            <div className='pt-40 sm:pt-50 md:pt-70'>
                                <Footer
                                    domain={domain}
                                    hideBorder={listing.totalPages > 1}
                                    menu={data?.footer} />
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

FilterList.propTypes = {
    appliedFilter: PropTypes.object,
    filterOption: PropTypes.object,
    index: PropTypes.number,
    setFilter: PropTypes.func,
    totalItems: PropTypes.number,
};

const propTypes = {
    data: PropTypes.shape({
        categories: PropTypes.array,
        items: PropTypes.shape({
            filters: PropTypes.object,
            items: PropTypes.array,
            page: PropTypes.number,
            total: PropTypes.number,
            totalPages: PropTypes.number,
        }),
        page: PropTypes.object,
        footer: PropTypes.shape({
            items: PropTypes.array,
        }),
        search: PropTypes.object,
        navigation: PropTypes.object,
    }),
    domain: PropTypes.string,
};

ResourcesListingPage.propTypes = propTypes;

export default ResourcesListingPage;
